import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { NavHeader } from "../components/NavHeader";
import { Footer } from "../components/Footer";
import { NotFoundPage } from "./NotFoundPage";
import { ZODIAC, ZODIAC_SLUGS } from "../data/zodiacContent";
import { Sparkles, Heart, Briefcase, Coins, Users } from "lucide-react";

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between items-baseline py-2 border-b border-white/[0.06]">
    <dt className="text-stardust/70 uppercase tracking-[0.2em] text-[10px]">{label}</dt>
    <dd className="text-slate-200 text-sm sm:text-base text-right">{value}</dd>
  </div>
);

export const ZodiacSignPage = () => {
  const { sign } = useParams();
  const slug = (sign || "").toLowerCase();
  const s = ZODIAC[slug];

  useEffect(() => {
    if (!s) return;
    document.title = `${s.seo_title} · Astrarium`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", s.seo_desc);
    window.scrollTo(0, 0);
  }, [s]);

  if (!s) return <NotFoundPage />;

  const idx = ZODIAC_SLUGS.indexOf(slug);
  const prevSlug = ZODIAC_SLUGS[(idx - 1 + 12) % 12];
  const nextSlug = ZODIAC_SLUGS[(idx + 1) % 12];

  return (
    <>
      <NavHeader />
      <main
        data-testid={`zodiac-page-${slug}`}
        className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12"
      >
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-8 text-[11px] uppercase tracking-[0.25em] text-slate-500">
          <Link to="/zodiac" className="hover:text-stardust transition-colors" data-testid="zodiac-breadcrumb-hub">
            The twelve signs
          </Link>
          <span className="mx-3 text-stardust/40">/</span>
          <span className="text-stardust/80">{s.name}</span>
        </nav>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-center mb-8">
            <div
              className="text-stardust text-7xl sm:text-8xl mb-4 inline-block animate-float-slow"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              {s.symbol + "\uFE0E"}
            </div>
            <h1 className="font-display text-5xl sm:text-7xl text-[#F5EBD8] leading-none mb-3">{s.name}</h1>
            <p className="text-stardust/80 uppercase tracking-[0.35em] text-[11px] mb-2">{s.dates}</p>
            <p className="text-slate-500 uppercase tracking-[0.25em] text-[10px]">
              {s.element} · {s.modality} · Ruled by {s.ruling_planet}
            </p>
          </div>

          <p className="font-serif-italic text-slate-300/90 text-lg sm:text-2xl leading-relaxed text-center mb-12 max-w-2xl mx-auto">
            {s.essence}
          </p>
        </motion.div>

        {/* Strengths & shadows */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-4 w-4 text-stardust" />
              <p className="text-stardust/80 uppercase tracking-[0.25em] text-[10px]">Strengths</p>
            </div>
            <ul className="space-y-2.5">
              {s.strengths.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-300 text-sm leading-relaxed">
                  <span className="text-stardust mt-0.5 shrink-0">✦</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-4 w-4 text-stardust rotate-180" />
              <p className="text-stardust/80 uppercase tracking-[0.25em] text-[10px]">Shadows</p>
            </div>
            <ul className="space-y-2.5">
              {s.shadows.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-300 text-sm leading-relaxed">
                  <span className="text-stardust/60 mt-0.5 shrink-0">✦</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Love */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Heart className="h-4 w-4 text-stardust" />
            <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px]">In love</p>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-4 leading-tight">
            How {s.name} loves.
          </h2>
          <p className="text-slate-300 leading-relaxed text-base sm:text-lg">{s.love}</p>
        </section>

        {/* Career */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Briefcase className="h-4 w-4 text-stardust" />
            <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px]">At work</p>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-4 leading-tight">
            Where {s.name} does its best work.
          </h2>
          <p className="text-slate-300 leading-relaxed text-base sm:text-lg">{s.career}</p>
        </section>

        {/* Money */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Coins className="h-4 w-4 text-stardust" />
            <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px]">With money</p>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-4 leading-tight">
            {s.name} and money.
          </h2>
          <p className="text-slate-300 leading-relaxed text-base sm:text-lg">{s.money}</p>
        </section>

        {/* Compatibility */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Users className="h-4 w-4 text-stardust" />
            <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px]">Compatibility</p>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-4 leading-tight">
            Signs {s.name} tends to match.
          </h2>
          <p className="text-slate-300 leading-relaxed text-base sm:text-lg mb-5">
            Traditional astrology reads {s.name} as harmonising most naturally with these three signs. It is not the whole story (rising sign, moon, and life stage shift the picture) but it is a strong starting frame.
          </p>
          <div className="flex flex-wrap gap-3">
            {s.allies.map((ally) => {
              const allySlug = ally.toLowerCase();
              return (
                <Link
                  key={ally}
                  to={`/zodiac/${allySlug}`}
                  data-testid={`zodiac-ally-${allySlug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stardust/[0.06] border border-stardust/20 text-stardust hover:bg-stardust/[0.12] hover:border-stardust/50 transition-all text-sm"
                >
                  <span style={{ fontFamily: "'DM Serif Display', serif" }}>{ZODIAC[allySlug]?.symbol + "\uFE0E"}</span>
                  {ally}
                </Link>
              );
            })}
          </div>
          <p className="text-slate-400 leading-relaxed mt-6 text-sm sm:text-base">
            Want a real reading, not just the sun-sign shortcut?{" "}
            <Link to="/" className="text-stardust hover:underline">Draw your full Astrarium almanac</Link>{" "}
            based on your exact birth date and place. Or run a{" "}
            <Link to="/" className="text-stardust hover:underline">pair synastry for the two of you</Link>{" "}
            with someone specific.
          </p>
        </section>

        {/* Lucky forecast */}
        <section className="mb-12 p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-stardust/[0.06] to-transparent border border-stardust/20">
          <p className="text-stardust/80 uppercase tracking-[0.4em] text-[10px] mb-4">A favourable forecast for {s.name}</p>
          <h2 className="font-display text-2xl sm:text-3xl text-[#F5EBD8] mb-6 leading-tight">
            Six small anchors of fortune.
          </h2>
          <dl className="space-y-1">
            <InfoRow label="Lucky day" value={s.lucky_day} />
            <InfoRow label="Lucky colours" value={s.lucky_colors.join(", ")} />
            <InfoRow label="Lucky numbers" value={s.lucky_numbers.join(", ")} />
            <InfoRow label="Lucky gemstones" value={s.lucky_gemstones.join(", ")} />
            <InfoRow label="Auspicious direction" value={s.lucky_direction} />
            <InfoRow label="Power mantra" value={<span className="italic">{s.mantra}</span>} />
          </dl>
        </section>

        {/* Famous */}
        <section className="mb-12">
          <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px] mb-3">Well-known {s.name}s</p>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-5 leading-tight">
            You share a birthday season with{" "}
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {s.famous_people.map((p) => (
              <li key={p} className="text-slate-300 text-sm sm:text-base leading-relaxed flex items-start gap-2">
                <span className="text-stardust mt-1 shrink-0">✦</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="text-center py-10 border-t border-white/[0.06]">
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-4 leading-tight">
            This is just the sun-sign layer.
          </h2>
          <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto mb-8">
            An Astrarium reading pulls in your date, place, and time of birth to draw a full almanac across seven chapters. Love, career, family, finances, health, the year ahead, and the strengths and shadows you actually live with.
          </p>
          <Link
            to="/"
            data-testid={`zodiac-cta-${slug}`}
            className="inline-flex items-center bg-stardust hover:bg-stardust-light text-cosmic-900 rounded-md uppercase tracking-[0.3em] text-xs px-10 py-5 font-medium shadow-[0_0_28px_rgba(226,194,117,0.25)] hover:shadow-[0_0_44px_rgba(226,194,117,0.45)] transition-all"
          >
            Draw my almanac ✦
          </Link>
        </section>

        {/* Prev / next */}
        <nav className="mt-14 flex items-center justify-between gap-4 text-[11px] uppercase tracking-[0.25em]" aria-label="Zodiac navigation">
          <Link
            to={`/zodiac/${prevSlug}`}
            data-testid="zodiac-prev-link"
            className="text-slate-400 hover:text-stardust transition-colors flex items-center gap-2"
          >
            <span>←</span>
            <span>{ZODIAC[prevSlug].name}</span>
          </Link>
          <Link to="/zodiac" className="text-slate-500 hover:text-stardust transition-colors" data-testid="zodiac-back-hub">
            All signs
          </Link>
          <Link
            to={`/zodiac/${nextSlug}`}
            data-testid="zodiac-next-link"
            className="text-slate-400 hover:text-stardust transition-colors flex items-center gap-2"
          >
            <span>{ZODIAC[nextSlug].name}</span>
            <span>→</span>
          </Link>
        </nav>
      </main>
      <Footer />
    </>
  );
};
